import format from 'date-fns/format'
import sub from 'date-fns/sub'

import { TBookingContextDef } from '@providers/booking/types'
import { buildPetGroups, buildPetGroupsTempered, sortOrders } from '@shared/booking_groups'
import type { TRouteNames } from 'app/types'
import { dayToJSDate } from 'components-ui'
import { Services, Types } from 'service-api'
import { daycareSelectionPeriod } from './pet_addons'
import { captureException } from 'service-api/src/shared'
import { hasGroomingServiceType } from './offers'

class BookingSearchError extends Error {
  constructor(message: string, originalError: Error) {
    super(message)
    this.name = 'BookingSearchError'
    this.stack = originalError.stack
  }
}

export const continueNavigation = async (
  bookingCtx: TBookingContextDef,
  accountName: string
): Promise<TRouteNames | undefined> => {
  switch (bookingCtx.state.currentState) {
    // if we are on search results, we need to check if there are offers or petAddOns to know
    // where to jump next, to keep browser history clean
    case 'searchResults': {
      let rc = await checkOfferAddOns(bookingCtx, accountName)
      if (rc) {
        return rc
      }
      rc = await checkPetAddOns(bookingCtx, accountName)
      if (rc) {
        return rc
      }
      if (isOfferWithTemperTest(bookingCtx)) {
        return 'temperTest'
      }
      return 'detailsPolicies'
    }
    /// page that show the results of the search for grooming
    case 'searchResultsGrooming': {
      return bookingCtx.state.config.serviceType?.requestDates === 'REQUIRED' ? 'requestDates' : 'detailsPolicies'
    }
    case 'requestDates': {
      return 'detailsPolicies'
    }
    // if we are on offerAddOns, we need to check if there are petAddOns to jump to that page
    // or skip it
    case 'roomAddOns': {
      const rc = await checkPetAddOns(bookingCtx, accountName)
      if (rc) {
        return rc
      }
      // if there are no petAddOns check if the offer requires temper test
      if (isOfferWithTemperTest(bookingCtx)) {
        return 'temperTest'
      }
      // if not move to details
      return 'detailsPolicies'
    }
    case 'petAddOns': {
      removePerksWithZeroQuantity(bookingCtx)
      // check if the offer requires temper test
      if (isOfferWithTemperTest(bookingCtx)) {
        return 'temperTest'
      }
      // if not move to details
      return 'detailsPolicies'
    }
    // dates selection for DayCare
    case 'datesSelection': {
      const rc = await checkPetAddOnsDaycare(bookingCtx, accountName)
      if (rc) {
        return rc
      }
      // if (!bookingCtx.state.groups?.[0]?.pets?.[0].temperTested) {
      if (isOfferWithTemperTest(bookingCtx)) {
        return 'temperTest'
      }
      return 'detailsPolicies'
    }
    case 'temperTest': {
      return 'detailsPolicies'
    }
    case 'detailsPolicies': {
      return 'paymentForm'
    }
  }

  return
}

export const removePerksWithZeroQuantity = (bookingCtx: TBookingContextDef) => {
  bookingCtx.state.cart.groups.forEach((group, groupIdx) => {
    group.petsAddOn?.forEach((petAddOn) => {
      petAddOn.pets.forEach((pet) => {
        if (pet.qty === 0) {
          bookingCtx.dispatch({
            type: 'REMOVE_PET_ADDON',
            payload: {
              groupIdx,
              petAddOnId: petAddOn.offer.id,
              petId: pet.id,
            },
          })
        }
      })
    })
  })
}

const isOfferWithTemperTest = (bookingCtx: TBookingContextDef) => {
  return bookingCtx.state.cart.groups.some((g) => {
    return (
      g.offerSelected?.availability.availabilities?.[0].condition?.reason?.type === 'TEMPER_TEST' ||
      g.daycareSelected?.some((day) => day.availability.availabilities?.[0].condition?.reason?.type === 'TEMPER_TEST')
    )
  })
}

const filterAvailableAddOns = (results: Types.BookingSearchClientDto[]) =>
  results.filter(
    (addOn) => addOn.availabilityGroups?.some((group) => group.availabilities?.some((av) => !!av.available))
  )

const someAddOnsAvailable = (results: Types.BookingSearchByDaysAdminDto[]) =>
  results.some(
    (addOn) => addOn.availabilityGroupsByDays?.some((group) => group[0].availabilities?.some((av) => av.available))
  )

const checkOfferAddOns = async (
  bookingCtx: TBookingContextDef,
  accountName: string
): Promise<TRouteNames | undefined> => {
  const serviceTypeName = bookingCtx.state.serviceTypeName
  const services = await Services.ClientBookingSearchService.search(accountName, serviceTypeName, {
    ...buildSearchParams(bookingCtx, true),
    offersType: 'SERVICE',
  })
  const availableAddOns = filterAvailableAddOns(services.results)
  if (availableAddOns.length > 0) {
    bookingCtx.dispatch({
      type: 'LOAD_OFFERS_ADDONS',
      payload: { value: sortOrders(availableAddOns), searchState: 'roomAddOns' },
    })
    return 'searchRoomAddOns'
  }
  return
}

const performSearch = async (
  locationName: string,
  serviceTypeName: string,
  params: Types.TBookingSearchOptions,
  offersType: string
): Promise<Types.BookingSearchClientDto[] | Types.BookingSearchByDaysAdminDto[] | undefined> => {
  try {
    if (serviceTypeName === 'daycare' && offersType === 'PET') {
      const result = await Services.ClientBookingSearchService.searchByDays(locationName, serviceTypeName, {
        ...params,
        offersType,
      } as Types.TBookingSearchOptions)
      return result.results
    } else {
      const result = await Services.ClientBookingSearchService.search(locationName, serviceTypeName, {
        ...params,
        offersType,
      } as Types.TBookingSearchOptions)
      return result.results
    }
  } catch (error) {
    captureException(new BookingSearchError('Error looking for offers', error as Error))
    return undefined
  }
}

const checkGroomingServices = async (
  bookingCtx: TBookingContextDef,
  locationName: string,
  baseParams: Types.TBookingSearchOptions
): Promise<Types.BookingSearchClientDto[]> => {
  if (!hasGroomingServiceType(bookingCtx)) {
    return []
  }

  const params = { ...baseParams } as Types.TBookingSearchOptions
  if (bookingCtx.state.serviceTypeName === 'daycare') {
    const dayPeriod = daycareSelectionPeriod(bookingCtx)
    if (dayPeriod) {
      const groomStartDate = sub(dayToJSDate(dayPeriod.endDate) || 0, { days: 1 })
      params.startDate = format(groomStartDate, 'yyyy-MM-dd')
    }
  }

  params.petGroups = buildPetGroups(bookingCtx.state, false, true)
  params.offersType = 'PRIMARY'

  try {
    const result = await Services.ClientBookingSearchService.search(locationName, 'grooming', params)
    return result.results || []
  } catch (error) {
    captureException(new BookingSearchError('Error looking for offers', error as Error))
    return []
  }
}

const checkPetAddOns = async (
  bookingCtx: TBookingContextDef,
  locationName: string
): Promise<TRouteNames | undefined> => {
  const serviceTypeName = bookingCtx.state.serviceTypeName
  const searchParams = buildSearchParams(bookingCtx, true)

  const [petAddOns, groomingServices] = await Promise.all([
    performSearch(locationName, serviceTypeName, searchParams as Types.TBookingSearchOptions, 'PET'),
    checkGroomingServices(bookingCtx, locationName, searchParams as Types.TBookingSearchOptions),
  ])

  const availablePetAddOns = petAddOns ? filterAvailableAddOns(petAddOns as Types.BookingSearchClientDto[]) : []

  if (availablePetAddOns.length > 0 || groomingServices.length > 0) {
    bookingCtx.dispatch({
      type: 'LOAD_PET_ADDONS',
      payload: {
        value: sortOrders(availablePetAddOns),
        searchState: 'petAddOns',
        grooms: groomingServices,
      },
    })
    return 'searchPetAddOns'
  }

  return undefined
}

const checkPetAddOnsDaycare = async (
  bookingCtx: TBookingContextDef,
  locationName: string
): Promise<TRouteNames | undefined> => {
  const serviceTypeName = bookingCtx.state.serviceTypeName
  const dayPeriod = daycareSelectionPeriod(bookingCtx)

  if (!dayPeriod) {
    return undefined
  }

  const searchParams = {
    petGroups: buildPetGroups(bookingCtx.state, true),
    offersType: 'PET',
    startDate: dayPeriod.startDate,
    endDate: dayPeriod.endDate,
  } as Types.TBookingSearchOptions

  const [petAddOns, groomingServices] = await Promise.all([
    performSearch(locationName, serviceTypeName, searchParams, 'PET'),
    checkGroomingServices(bookingCtx, locationName, searchParams),
  ])

  const hasAvailablePetAddOns = petAddOns
    ? someAddOnsAvailable(petAddOns as Types.BookingSearchByDaysAdminDto[])
    : false

  if (hasAvailablePetAddOns || groomingServices.length > 0) {
    bookingCtx.dispatch({
      type: 'LOAD_PET_ADDONS',
      payload: {
        value: sortOrders(petAddOns || []),
        searchState: 'petAddOns',
        grooms: groomingServices,
      },
    })
    return 'searchPetAddOns'
  }

  return undefined
}

export const buildSearchParams = (
  bookingCtx: TBookingContextDef,
  isAddOnSearch?: boolean,
  isGroom?: boolean,
  groomId?: string
) => {
  return {
    startDate: format(bookingCtx.state.startDate || 0, 'yyyy-MM-dd'),
    endDate: format(bookingCtx.state.endDate || 0, 'yyyy-MM-dd'),
    petGroups: buildPetGroups(bookingCtx.state, isAddOnSearch, isGroom, groomId),
  }
}
export const buildSearchParamsForTemperTest = (
  bookingCtx: TBookingContextDef,
  today: Date,
  tempTestRange: Date
): Types.TBookingSearchOptions => {
  return {
    startDate: format(today || 0, 'yyyy-MM-dd'),
    endDate: format(tempTestRange || 0, 'yyyy-MM-dd'),
    petGroups: buildPetGroupsTempered(bookingCtx.state),
    offersType: 'PRIMARY',
  }
}
